import { DEFAULT_DOT_OPACITY, PROBIT_FIT_PATTERN } from "constants/chart.constants";
import { SeriesType } from "types/echarts";
import { EvaChartStates } from "types/factory";
import { removeProbitInfoFromtitle } from "utils/removeProbitInfoFromTitle";

import {
  BLUR_OPACITY,
  DEFAULT_Z_INDEX,
  HIGHLIGHTED_Z_INDEX,
  PROBIT_HIGHLIGHTED_Z_INDEX
} from "../../translators/constants";

function getHoverOptions(
  isHoveringOnBins: boolean,
  isHoveringOnChart: boolean,
  isHoveringOnMap: boolean,
  hoverLegendItem: string,
  selectedGroups: string[],
  seriesName: string,
  pointOpacityValue: number,
  getSeriesStyle: (
    label: string,
    originalStyle?: { thickness?: number; opacity?: number }
  ) => { thickness: number; opacity: number },
  getHighlightedSeriesStyle: (label: string) => { thickness: number; opacity: number }
): { dotOpacity: number; lineOpacity: number; lineWidth: number } {
  let dotOpacity = pointOpacityValue;
  const currentStyle = getSeriesStyle(seriesName);
  let lineOpacity = currentStyle.opacity;
  let lineWidth = currentStyle.thickness;

  const isHighlighted =
    seriesName === hoverLegendItem || (selectedGroups?.includes?.(seriesName) ?? false);
  if (
    isHoveringOnBins ||
    isHoveringOnMap ||
    isHoveringOnChart ||
    selectedGroups.length > 0
  ) {
    dotOpacity = BLUR_OPACITY;
    lineOpacity = BLUR_OPACITY;
    if (isHighlighted) {
      const highlightedStyle = getHighlightedSeriesStyle(seriesName);
      lineOpacity = highlightedStyle.opacity;
      lineWidth = highlightedStyle.thickness;
      dotOpacity = isHoveringOnBins || isHoveringOnMap ? 1 : BLUR_OPACITY;
    }
  }

  return {
    dotOpacity,
    lineOpacity,
    lineWidth
  };
}

export function probitBalanceHighlightedSeries(states: Partial<EvaChartStates>) {
  const {
    series,
    hoverLegendItem,
    hoverLegendGroup,
    selectedGroups = [],
    attentionWells = [],
    pointOpacity,
    syncWells,
    getSeriesStyle,
    getHighlightedSeriesStyle,
    getPointStyle,
    useLineOfBestFit
  } = states;

  const pointOpacityValue = pointOpacity?.properties?.value ?? DEFAULT_DOT_OPACITY;
  const { size: pointSizeValue } = getPointStyle();

  const isHoveringOnBins = hoverLegendItem && attentionWells?.length === 0;
  const isHoveringOnChart =
    hoverLegendItem && hoverLegendGroup && hoverLegendGroup !== hoverLegendItem;
  const isHoveringOnMap = hoverLegendItem && attentionWells?.length > 0;
  const highlightSyncedWell =
    !hoverLegendItem && syncWells?.isSync && syncWells?.activeWells?.length > 0;
  const showLinesOfBestFit = useLineOfBestFit?.active;

  return series
    .filter((s) => {
      // Filter out series with labels matching the fit pattern only if showLinesOfBestFit is false
      // Pattern: "Fit - P90:X,P50:X,Mean:X,Swanson's Mean:X,P10:X,P10/P90:X"
      if (showLinesOfBestFit) {
        return true; // Keep all series when showLinesOfBestFit is true
      }
      return !s.label || !s.label.match(PROBIT_FIT_PATTERN);
    })
    .map((s, index) => {
      const { label, mode } = s;

      const type = mode === "markers" ? SeriesType.Scatter : mode;
      const seriesName = removeProbitInfoFromtitle(label);
      const hoverItem = removeProbitInfoFromtitle(hoverLegendItem);

      const isHoveringLine = attentionWells?.length === 0 && hoverItem === seriesName;
      const isHoveredOnFitLine =
        hoverLegendItem.includes("Fit -") && attentionWells?.length > 0;

      const { dotOpacity, lineOpacity, lineWidth } = getHoverOptions(
        isHoveringOnBins,
        isHoveringOnChart,
        isHoveringOnMap,
        hoverItem,
        selectedGroups,
        seriesName,
        pointOpacityValue,
        getSeriesStyle,
        getHighlightedSeriesStyle
      );

      if (type === SeriesType.Scatter) {
        if (highlightSyncedWell) {
          return {
            id: `${label}, ${index}`,
            itemStyle: {
              opacity: BLUR_OPACITY
            },
            symbolSize: pointSizeValue,
            z: isHoveredOnFitLine ? HIGHLIGHTED_Z_INDEX : DEFAULT_Z_INDEX
          };
        }
        return {
          id: `${label}, ${index}`,
          itemStyle: {
            opacity: isHoveredOnFitLine ? BLUR_OPACITY : dotOpacity
          }
        };
      } else if (type === SeriesType.Line) {
        return {
          id: `${label}, ${index}`,
          lineStyle: {
            width: lineWidth,
            opacity: lineOpacity
          },
          z: isHoveringLine ? PROBIT_HIGHLIGHTED_Z_INDEX : DEFAULT_Z_INDEX
        };
      }
    });
}
